import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Heart, Banknote, Building2, Check, Loader2, MessageCircle, Copy, ExternalLink, X } from 'lucide-react';
import { apiFetch } from '../../lib/api';
import type { DonationCreate } from '../../types';
import { useAppStore } from '../../store/appStore';
import { useTypewriter } from '../../hooks/useTypewriter';
import UpsellModal from './UpsellModal';

const MONTHLY_TYPEWRITER_TEXT = '因為我們相信，真正的善🫶🏻，源於自由的心💓，而非壓力的手🫱🏻。';

function MonthlyTypewriter() {
  const { display, showCursor } = useTypewriter(MONTHLY_TYPEWRITER_TEXT, 200, 2000, 3000);
  return (
    <span className="text-[16px] animate-flashlight-slow">
      {display}
      <span className={`inline-block w-[2px] h-3.5 ml-[1px] align-middle bg-brand-600 ${showCursor ? 'opacity-100' : 'opacity-0'}`} />
    </span>
  );
}

export default function DonationForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { selectedAmount, isMonthly, setSelectedAmount, setIsMonthly } = useAppStore();
  const [referrerCode, setReferrerCode] = useState<string>('');
  const [settings, setSettings] = useState<Record<string, string>>({});

  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) {
      setReferrerCode(ref);
    }
    const monthly = searchParams.get('monthly');
    if (monthly === 'true') {
      setIsMonthly(true);
    }
  }, [searchParams, setIsMonthly]);

  useEffect(() => {
    apiFetch<Record<string, string>>('/api/public/settings')
      .then((res) => {
        if (res.success && res.data) {
          setSettings(res.data);
        }
      })
      .catch(() => {});
  }, []);

  const [customAmount, setCustomAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<DonationCreate['paymentMethod'] | null>(null);
  const [formData, setFormData] = useState({
    donorName: '',
    donorEmail: '',
    message: '',
  });
  const [options, setOptions] = useState({
    isAnonymous: false,
    displayOnWall: true,
    receiptRequested: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showUpsell, setShowUpsell] = useState(false);
  const [upsellAmount, setUpsellAmount] = useState(0);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [verifyCode, setVerifyCode] = useState('');
  const [verifyStatus, setVerifyStatus] = useState<'pending' | 'confirmed' | 'expired'>('pending');
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const amountOptions = settings.donateAmountOptions
    ? settings.donateAmountOptions.split(',').map((s) => parseInt(s.trim(), 10)).filter((n) => !isNaN(n))
    : [50, 200, 500, 1000];

  const activeAmount = selectedAmount ?? (customAmount ? Number(customAmount) : null);
  const isCustom = selectedAmount === null && customAmount !== '';

  const handleAmountSelect = (amount: number) => {
    setSelectedAmount(amount);
    setCustomAmount('');
    if (amount === 50 || amount === 200) {
      setUpsellAmount(amount);
      setShowUpsell(true);
    }
  };

  const handleUpgrade = (amount: number) => {
    setSelectedAmount(amount);
    setCustomAmount('');
    setShowUpsell(false);
  };

  const handleCustomAmountChange = (val: string) => {
    if (/^\d*$/.test(val)) {
      setCustomAmount(val);
      setSelectedAmount(null);
    }
  };

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!activeAmount || activeAmount <= 0) {
      setError('請選擇或輸入捐款金額');
      return;
    }
    if (!paymentMethod) {
      setError('請選擇支付方式');
      return;
    }
    if (!formData.donorName.trim()) {
      setError('請輸入您的姓名');
      return;
    }
    setIsSubmitting(true);

    const payload: DonationCreate = {
      donorName: formData.donorName.trim(),
      donorEmail: formData.donorEmail.trim() || undefined,
      amount: activeAmount * 100,
      donationType: isMonthly ? 'monthly-first' : 'one-time',
      paymentMethod,
      message: formData.message.trim() || undefined,
      isAnonymous: options.isAnonymous,
      displayOnWall: options.displayOnWall,
      receiptRequested: options.receiptRequested,
      campaignSource: 'website',
      referrerCode: referrerCode || undefined,
    };

    const res = await apiFetch<{ verifyCode: string; waLink: string; expirySeconds: number }>('/api/public/donation-draft', {
      method: 'POST',
      body: JSON.stringify(payload),
    });

    setIsSubmitting(false);

    if (res.success && res.data?.verifyCode) {
      setVerifyCode(res.data.verifyCode);
      setVerifyStatus('pending');
      setShowVerifyModal(true);
      // Open WhatsApp - use location.href on mobile (more reliable), window.open on desktop
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      if (isMobile) {
        window.location.href = res.data.waLink;
      } else {
        window.open(res.data.waLink, '_blank');
      }
      // Start polling
      startPolling(res.data.verifyCode);
    } else {
      setError(res.error?.message || '提交失敗，請重試');
    }
  };

  const startPolling = (code: string) => {
    stopPolling();
    const startTime = Date.now();
    const maxDuration = 10 * 60 * 1000; // 10 minutes

    pollRef.current = setInterval(async () => {
      if (Date.now() - startTime > maxDuration) {
        stopPolling();
        setVerifyStatus('expired');
        return;
      }

      const res = await apiFetch<{ status: string; donationNum?: string }>(`/api/public/donation-by-code?code=${encodeURIComponent(code)}`);
      console.log('[POLL] donation-by-code response:', JSON.stringify(res));
      if (res.success && res.data) {
        console.log('[POLL] status:', res.data.status, 'donationNum:', res.data.donationNum);
        if (res.data.status === 'confirmed' && res.data.donationNum) {
          console.log('[POLL] Navigating to success page');
          stopPolling();
          setVerifyStatus('confirmed');
          setShowVerifyModal(false);
          navigate(`/success?dn=${res.data.donationNum}`);
        }
        // If pending, keep polling
      } else if (res.error?.code === 'NOT_FOUND') {
        stopPolling();
        setVerifyStatus('expired');
      }
    }, 3000);
  };

  const handleCloseVerifyModal = () => {
    stopPolling();
    setShowVerifyModal(false);
  };

  const handleOpenWhatsApp = () => {
    const businessPhone = '85262322466';
    const waMessage = `【好餸傳愛捐款驗證】${verifyCode}`;
    const waLink = `https://wa.me/${businessPhone}?text=${encodeURIComponent(waMessage)}`;
    window.open(waLink, '_blank');
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6" id="donate">
      {/* 表單標題 */}
      {(settings.donateFormTitle || settings.donateFormDescription) && (
        <div className="text-center pb-2">
          {settings.donateFormTitle && (
            <h2 className="text-2xl font-bold text-gray-800">{settings.donateFormTitle}</h2>
          )}
          {settings.donateFormDescription && (
            <p className="mt-2 text-gray-600">{settings.donateFormDescription}</p>
          )}
        </div>
      )}

      {/* 金額選項 */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-3">
          選擇捐款金額
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {amountOptions.map((amount) => (
            <button
              key={amount}
              type="button"
              onClick={() => handleAmountSelect(amount)}
              className={`relative p-4 rounded-2xl border-2 text-center transition-all min-h-[80px] flex flex-col items-center justify-center ${
                selectedAmount === amount
                  ? 'border-brand-600 bg-brand-50 text-brand-800'
                  : 'border-gray-200 bg-white text-gray-700 hover:border-brand-300'
              }`}
            >
              <span className="text-lg font-bold">${amount.toLocaleString()}</span>
              {selectedAmount === amount && (
                <span className="absolute top-2 right-2 w-5 h-5 bg-brand-600 rounded-full flex items-center justify-center">
                  <Check className="w-3 h-3 text-white" />
                </span>
              )}
            </button>
          ))}
        </div>
        <div className="mt-3">
          <input
            type="text"
            inputMode="numeric"
            placeholder="自訂金額 HK$"
            value={customAmount}
            onChange={(e) => handleCustomAmountChange(e.target.value)}
            className={`w-full p-4 rounded-2xl border-2 text-center font-bold text-lg transition-all outline-none ${
              isCustom
                ? 'border-brand-600 bg-brand-50 text-brand-800'
                : 'border-gray-200 bg-white text-gray-700 focus:border-brand-400'
            }`}
          />
        </div>
      </div>

      {/* 月捐切換 */}
      <div className="bg-brand-50 rounded-2xl p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Heart className={`w-5 h-5 flex-shrink-0 ${isMonthly ? 'text-brand-600 fill-brand-600' : 'text-gray-400'}`} />
            <div>
              <span className="text-sm font-semibold text-gray-800">
                {isMonthly
                  ? (settings.donateMonthlyLabel || '成為改變的槓桿 — 每月與我們同行')
                  : (settings.donateOnetimeLabel || '一次性捐款')}
              </span>
              <p className="text-xs text-gray-500">
                {isMonthly
                  ? (settings.donateMonthlyDesc || '不用綁定，不用承諾，只需一顆想持續行善的心')
                  : (settings.donateOnetimeDesc || '感謝您的支持，每一分都帶來改變')}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setIsMonthly(!isMonthly)}
            className={`relative w-12 h-7 rounded-full transition-colors flex-shrink-0 ${
              isMonthly ? 'bg-brand-600' : 'bg-gray-300'
            }`}
          >
            <span
              className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform ${
                isMonthly ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
        </div>
        {isMonthly && (
          <div className="mt-3 pt-3 border-t border-brand-200/60 text-sm text-brand-700 leading-relaxed">
            <p>
              {settings.donateMonthlyDesc || `每月經 WhatsApp 與您分享真實故事 — 您會親眼看見，您的善行如何像槓桿一樣， 撬動整個社區的改變。`}
            </p>
            <p className="mt-2 font-bold">
              ❌沒有自動扣款，❌沒有綁定合約。隨時可以暫停或調整。
            </p>
            <p className="mt-2 font-bold">
              <MonthlyTypewriter />
            </p>
          </div>
        )}
      </div>

      {/* 支付方式 */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-3">
          支付方式
        </label>
        <div className="grid grid-cols-2 gap-3">
          {[
            { id: 'fps' as const, label: 'FPS', icon: Banknote },
            { id: 'bank-transfer' as const, label: '銀行轉賬', icon: Building2 },
          ].map((pm) => (
            <button
              key={pm.id}
              type="button"
              onClick={() => setPaymentMethod(pm.id)}
              className={`p-4 rounded-2xl border-2 text-center transition-all min-h-[72px] flex flex-col items-center justify-center gap-1 ${
                paymentMethod === pm.id
                  ? 'border-brand-600 bg-brand-50 text-brand-800'
                  : 'border-gray-200 bg-white text-gray-600 hover:border-brand-300'
              }`}
            >
              <pm.icon className="w-5 h-5" />
              <span className="text-sm font-medium">{pm.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* 捐贈者信息 */}
      <div className="space-y-4">
        <label className="block text-sm font-semibold text-gray-700">
          捐贈者信息
        </label>
        <input
          type="text"
          placeholder="姓名 *"
          required
          value={formData.donorName}
          onChange={(e) => setFormData({ ...formData, donorName: e.target.value })}
          className="w-full p-4 rounded-xl border border-gray-200 bg-white text-gray-800 outline-none focus:border-brand-400 transition-colors"
        />
        <input
          type="email"
          placeholder="電郵"
          value={formData.donorEmail}
          onChange={(e) => setFormData({ ...formData, donorEmail: e.target.value })}
          className="w-full p-4 rounded-xl border border-gray-200 bg-white text-gray-800 outline-none focus:border-brand-400 transition-colors"
        />
      </div>

      {/* 選項 */}
      <div className="space-y-3">
        {[
          { key: 'isAnonymous' as const, label: '匿名捐贈' },
          { key: 'displayOnWall' as const, label: '在感謝牆顯示我的名字' },
          { key: 'receiptRequested' as const, label: '索取扣稅收據' },
        ].map((opt) => (
          <label key={opt.key} className="flex items-center gap-3 cursor-pointer">
            <div
              className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                options[opt.key]
                  ? 'bg-brand-600 border-brand-600'
                  : 'border-gray-300 bg-white'
              }`}
            >
              {options[opt.key] && <Check className="w-3.5 h-3.5 text-white" />}
            </div>
            <input
              type="checkbox"
              className="hidden"
              checked={options[opt.key]}
              onChange={(e) =>
                setOptions({ ...options, [opt.key]: e.target.checked })
              }
            />
            <span className="text-sm text-gray-700">{opt.label}</span>
          </label>
        ))}
      </div>

      {/* 留言 */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          感謝墻留言（選填）
        </label>
        <textarea
          rows={3}
          placeholder="寫下您的祝福或支持..."
          value={formData.message}
          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
          className="w-full p-4 rounded-xl border border-gray-200 bg-white text-gray-800 outline-none focus:border-brand-400 transition-colors resize-none"
        />
      </div>

      {/* 錯誤提示 */}
      {error && (
        <div className="p-4 rounded-xl bg-red-50 text-red-700 text-sm font-medium">
          {error}
        </div>
      )}

      {/* 提交按鈕 */}
      <button
        type="submit"
        disabled={isSubmitting}
        className="btn-primary w-full text-lg"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            處理中...
          </>
        ) : (
          <>
            <Heart className="w-5 h-5" />
            {activeAmount
              ? `WhatsApp 確認捐款 HK$${activeAmount.toLocaleString()}${isMonthly ? ' / 月' : ''}`
              : 'WhatsApp 確認捐款'}
          </>
        )}
      </button>

      {showUpsell && (
        <UpsellModal
          currentAmount={upsellAmount}
          onClose={() => setShowUpsell(false)}
          onUpgrade={handleUpgrade}
        />
      )}

      {/* WhatsApp 驗證等待模態框 */}
      {showVerifyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 text-center relative">
            <button
              onClick={handleCloseVerifyModal}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>

            {verifyStatus === 'pending' ? (
              <>
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MessageCircle className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">請在 WhatsApp 發送驗證碼</h3>
                <p className="text-gray-600 text-sm mb-4">
                  我們已為您準備好驗證碼，請在 WhatsApp 對話中發送即可確認捐款
                </p>
                <div className="bg-gray-50 rounded-xl p-4 mb-4 flex items-center justify-between">
                  <code className="text-2xl font-bold text-brand-600 tracking-widest">{verifyCode}</code>
                  <button
                    onClick={() => navigator.clipboard.writeText(verifyCode)}
                    className="p-2 hover:bg-gray-200 rounded-lg transition-colors text-gray-500"
                    title="複製驗證碼"
                  >
                    <Copy className="w-5 h-5" />
                  </button>
                </div>
                <button
                  onClick={handleOpenWhatsApp}
                  className="btn-primary w-full mb-3 flex items-center justify-center gap-2"
                >
                  <ExternalLink className="w-4 h-4" />
                  打開 WhatsApp 發送
                </button>
                <p className="text-xs text-gray-400">
                  <Loader2 className="w-3 h-3 inline animate-spin mr-1" />
                  等待確認中…請勿關閉此頁面
                </p>
              </>
            ) : verifyStatus === 'expired' ? (
              <>
                <h3 className="text-xl font-bold text-gray-900 mb-2">驗證碼已過期</h3>
                <p className="text-gray-600 text-sm mb-4">驗證碼已過期，請重新提交捐款表單</p>
                <button
                  onClick={handleCloseVerifyModal}
                  className="btn-primary w-full"
                >
                  重新提交
                </button>
              </>
            ) : null}
          </div>
        </div>
      )}
    </form>
  );
}
